/*
  Every review of a product, ten at a time, with search and a star filter.

  snippets/atlas-review-summary.liquid prints the button and an empty panel and explains why
  this exists (Yotpo's own list is not running on this theme). This file fills the panel
  from Yotpo's public reviews API, the one Yotpo's widget itself reads:

    POST https://api.yotpo.com/v1/reviews/<store key>/filter.json
         { domain_key: <product id>, page, per_page, sortings, free_text_search?, scores? }

  Nothing is requested until the button is pressed. A review's words go into the page as
  text, never as markup, so nothing a customer typed can run here. A search term is marked
  in the results by splitting text nodes, for the same reason.

  If the request fails the panel says so and offers to try again; the three reviews printed
  by the server are above it either way.
*/
(() => {
  const PER_PAGE = 10;
  const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  const el = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };

  // The words of a review with every occurrence of the search term wrapped in <mark>.
  const marked = (text, term) => {
    const out = document.createDocumentFragment();
    const needle = term.trim().toLowerCase();
    if (!needle) {
      out.appendChild(document.createTextNode(text));
      return out;
    }
    const hay = text.toLowerCase();
    let from = 0;
    for (;;) {
      const at = hay.indexOf(needle, from);
      if (at < 0) break;
      out.appendChild(document.createTextNode(text.slice(from, at)));
      out.appendChild(el('mark', 'atlas-revall__hit', text.slice(at, at + needle.length)));
      from = at + needle.length;
    }
    out.appendChild(document.createTextNode(text.slice(from)));
    return out;
  };

  // The same card the server prints for the three above (snippets/atlas-review-card.liquid).
  const card = (review, term) => {
    const item = el('li', 'atlas-rev__review');
    const head = el('div', 'atlas-rev__reviewhead');
    const stars = el('span', 'atlas-rev__reviewstars');
    stars.setAttribute('aria-hidden', 'true');
    stars.appendChild(el('span', 'atlas-rev__stars-track', '★★★★★'));
    const fill = el('span', 'atlas-rev__stars-fill', '★★★★★');
    fill.style.width = `${Math.max(0, Math.min(5, review.score)) * 20}%`;
    stars.appendChild(fill);
    head.appendChild(stars);
    head.appendChild(el('span', 'visually-hidden', `${review.score} out of 5`));
    if (review.verified_buyer) head.appendChild(el('span', 'atlas-rev__verified', 'Verified buyer'));
    item.appendChild(head);

    const title = (review.title || '').trim();
    const body = (review.content || '').trim();
    // Yotpo fills a missing title with the first words of the review; do not say it twice.
    if (title && !body.toLowerCase().startsWith(title.toLowerCase())) {
      const heading = el('p', 'atlas-rev__reviewtitle');
      heading.appendChild(marked(title, term));
      item.appendChild(heading);
    }
    if (body) {
      const text = el('p', 'atlas-rev__reviewtext');
      text.appendChild(marked(body, term));
      item.appendChild(text);
    }

    const meta = el('p', 'atlas-rev__reviewmeta');
    const who = review.user && review.user.display_name;
    if (who) meta.appendChild(el('span', 'atlas-rev__reviewer', who));
    const when = new Date(review.created_at);
    if (!Number.isNaN(when.getTime())) {
      meta.appendChild(el('span', 'atlas-rev__reviewdate', `${MONTHS[when.getUTCMonth()]} ${when.getUTCFullYear()}`));
    }
    item.appendChild(meta);
    return item;
  };

  const wire = (root) => {
    if (root.dataset.wired) return;
    root.dataset.wired = 'true';

    const open = root.querySelector('[data-atlas-revall-open]');
    const openLabel = root.querySelector('[data-atlas-revall-open-label]');
    const panel = root.querySelector('.atlas-revall__panel');
    const form = root.querySelector('[data-atlas-revall-form]');
    const search = root.querySelector('[data-atlas-revall-search]');
    const starSelect = root.querySelector('[data-atlas-revall-stars]');
    const status = root.querySelector('[data-atlas-revall-status]');
    const list = root.querySelector('[data-atlas-revall-list]');
    const more = root.querySelector('[data-atlas-revall-more]');
    const closedLabel = openLabel.textContent;

    const state = { page: 0, total: 0, term: '', stars: '', busy: false, ticket: 0 };

    const describe = () => {
      const count = state.total.toLocaleString('en-US');
      const noun = state.total === 1 ? 'review' : 'reviews';
      const stars = state.stars ? ` with ${state.stars} ${state.stars === '1' ? 'star' : 'stars'}` : '';
      if (state.term) return state.total ? `${count} ${noun}${stars} mention “${state.term}”.` : `No reviews${stars} mention “${state.term}”.`;
      if (state.stars) return state.total ? `${count} ${noun}${stars}.` : `No reviews${stars}.`;
      return `${count} ${noun}, newest first.`;
    };

    const load = (fresh) => {
      if (state.busy && !fresh) return;
      const ticket = ++state.ticket;
      if (fresh) {
        state.page = 0;
        state.total = 0;
      }
      state.busy = true;
      more.disabled = true;
      root.classList.add('is-loading');
      if (fresh) status.textContent = 'Loading reviews…';

      const body = {
        domain_key: String(root.dataset.productId),
        page: state.page + 1,
        per_page: PER_PAGE,
        sortings: [{ sort_by: 'date', ascending: false }],
      };
      if (state.term) body.free_text_search = state.term;
      if (state.stars) body.scores = [Number(state.stars)];

      fetch(`https://api.yotpo.com/v1/reviews/${root.dataset.appKey}/filter.json`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
        .then((response) => {
          if (!response.ok) throw new Error(String(response.status));
          return response.json();
        })
        .then((data) => {
          if (ticket !== state.ticket) return; // a newer search has started
          const payload = data.response || data;
          const reviews = payload.reviews || [];
          if (fresh) list.replaceChildren();
          reviews.forEach((review) => list.appendChild(card(review, state.term)));
          state.page += 1;
          state.total = (payload.pagination && payload.pagination.total) || 0;
          status.textContent = describe();
          const shown = list.children.length;
          more.hidden = shown >= state.total || reviews.length === 0;
          more.textContent = `Show more reviews (${shown.toLocaleString('en-US')} of ${state.total.toLocaleString('en-US')})`;
        })
        .catch(() => {
          if (ticket !== state.ticket) return;
          status.textContent = 'The reviews could not be loaded just now.';
          more.hidden = false;
          more.textContent = 'Try again';
        })
        .finally(() => {
          if (ticket !== state.ticket) return;
          state.busy = false;
          more.disabled = false;
          root.classList.remove('is-loading');
        });
    };

    open.addEventListener('click', () => {
      const show = panel.hidden;
      panel.hidden = !show;
      open.setAttribute('aria-expanded', show ? 'true' : 'false');
      openLabel.textContent = show ? 'Hide the full list' : closedLabel;
      root.classList.toggle('is-open', show);
      if (show && state.page === 0) load(true);
    });

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      state.term = search.value.trim();
      state.stars = starSelect.value;
      load(true);
    });

    starSelect.addEventListener('change', () => {
      state.term = search.value.trim();
      state.stars = starSelect.value;
      load(true);
    });

    // Clearing the field with its own x is a search for everything.
    search.addEventListener('search', () => {
      if (search.value.trim() === '' && state.term !== '') {
        state.term = '';
        load(true);
      }
    });

    more.addEventListener('click', () => load(list.children.length === 0));
  };

  const run = () => document.querySelectorAll('[data-atlas-revall]').forEach(wire);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
  else run();
  document.addEventListener('shopify:section:load', run);
})();
